import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

/** Merchant fields the hub header needs. Lifecycle fields come from the merchant API. */
export interface HubMerchant {
  id: string;
  name: string;
  legal_name: string;
  email: string;
  country_code: string;
  country_name: string;
  is_active: boolean;
  blocked_at: string | null;
  block_reason: string;
  archived_at: string | null;
  archive_reason: string;
  fee_bearer: string;
  allow_merchant_fees: boolean;
  application_count: number;
  created_at: string;
}

/** Merchant profile: contact emails, phone, legal name and logo. */
export interface MerchantProfile {
  name: string;
  legal_name: string;
  email: string;
  notification_email: string;
  phone_number: string;
  logo: string;
}

/** Merchant lifecycle state derived from the merchant payload. */
export type MerchantState = 'active' | 'inactive' | 'blocked' | 'archived';

export function merchantState(
  merchant: Pick<HubMerchant, 'is_active' | 'blocked_at' | 'archived_at'>,
): MerchantState {
  if (merchant.archived_at) {
    return 'archived';
  }
  if (merchant.blocked_at) {
    return 'blocked';
  }
  return merchant.is_active ? 'active' : 'inactive';
}

// ---- Overview ----
export interface OperationTotals {
  total: number;
  success: number;
  failed: number;
  pending: number;
  success_rate: number | null;
  amounts: Record<string, string>;
}

export interface DailyOperation {
  count: number;
  success: number;
  amounts: Record<string, string>;
}

export interface OverviewResponse {
  days: number;
  since: string;
  totals: Record<'deposits' | 'payouts' | 'refunds' | 'remittances', OperationTotals>;
  daily: Array<
    { date: string } & Partial<
      Record<'deposits' | 'payouts' | 'refunds' | 'remittances', DailyOperation>
    >
  >;
  providers: Array<{ code: string; name: string; count: number; success: number }>;
  fees: Array<{ fee_type: string; currency_code: string; amount: string }>;
  wallets: Array<{
    id: string;
    currency_code: string;
    country_code: string;
    balance: string;
    reserved_balance: string;
    is_active: boolean;
  }>;
}

// ---- Fees ----
export interface FeeRuleSummary {
  id: string;
  name: string;
  scope: string;
  amount_type: string;
  value: string;
  currency_code: string;
  operation_type: string | null;
  composition_mode: string;
  collected_by_provider: boolean;
}

export interface FeeAmounts {
  provider_fee: string;
  provider_fee_declarative: boolean;
  our_fee: string;
  additional_fee: string;
  promotion: string;
  total: string;
}

export interface FeeOperationRow {
  operation_type: string;
  fees_enabled: boolean;
  current: FeeAmounts;
  default: FeeAmounts;
  overridden: boolean;
  sources: {
    provider_fee: FeeRuleSummary | null;
    our_fee: FeeRuleSummary | null;
    promotion: FeeRuleSummary | null;
    additional: FeeRuleSummary[];
  };
}

export interface FeeEntityBlock {
  code: string;
  name: string;
  country_code: string;
  country_name: string;
  currency_code: string;
  operations: FeeOperationRow[];
}

export interface FeeSwitchRow {
  operation_type: string;
  label: string;
  fees_enabled: boolean;
  reason: string;
  changed_at: string | null;
}

export interface FeesResponse {
  reference_amount: string;
  fee_bearer: string;
  allow_merchant_fees: boolean;
  entities: FeeEntityBlock[];
  switches: FeeSwitchRow[];
}

export interface FeeOverridePayload {
  fee_type: 'OUR_FEE' | 'PROVIDER_FEE' | 'PROMOTION';
  amount_type: 'FIXED' | 'PERCENTAGE';
  value: string;
  operation_type?: string | null;
  currency_code?: string;
  reason?: string;
}

export interface ServiceFeesResponse {
  rules: FeeRuleSummary[];
  totals: Array<{
    operation_type: string;
    currency_code: string;
    count: number;
    units: number;
    amount: string;
  }>;
  recent: Array<{
    reference: string;
    operation_type: string;
    units: number;
    amount: string;
    currency_code: string;
    status: string;
    created_at: string;
  }>;
}

// ---- Configuration ----
export interface CountryRef {
  id: string | number;
  code: string;
  name: string;
}

/** A value of the merchant `sdk` block: scalars plus the structured card settings and method list. */
export type SdkValue = string | boolean | number | string[] | Record<string, unknown> | null;

/** One page of a server-paginated list (20 per page at most). */
export interface HubPage<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ConfigurationCountry extends CountryRef {
  /** True when the merchant may operate in this country. */
  enabled: boolean;
  access: 'default' | 'allow' | 'disallow';
}

export interface ConfigurationProvider {
  id: string;
  country: CountryRef;
  access: 'default' | 'allow' | 'disallow';
  enabled: boolean;
  code: string;
  name: string;
  entity_type: string;
  currency_code: string;
  api_provider: string;
  routes: Array<{
    id: string;
    operation_types: string[];
    execution_provider: string;
    merchant_specific: boolean;
    excludes_merchant: boolean;
    maintenance_mode: boolean;
    priority: number;
  }>;
}

export interface ConfigurationProviderPage extends HubPage<ConfigurationProvider> {
  facets: { entity_types: string[]; currencies: string[] };
}

/** Switch items on or off without sending the whole allow/disallow list. */
export interface AccessDelta {
  enable?: Array<string | number>;
  disable?: Array<string | number>;
}

export interface AvailableMarket {
  id: number | string;
  code: string;
  name: string;
  currencies: Array<{
    market_code: string;
    currency_id: number | string;
    currency_code: string;
    has_wallet: boolean;
  }>;
}

export interface HubWallet {
  id: string;
  country_id: number | string;
  country_code: string;
  country_name: string;
  currency_code: string;
  balance: string;
  is_active: boolean;
  additional_operation_markets: CountryRef[];
}

export interface ConfigurationResponse {
  sdk: Record<string, SdkValue>;
  locale_choices: Array<{ value: string; label: string }>;
  /** Counts only: the lists are paged through `configurationCountries` / `configurationProviders`. */
  countries: { allowed_count: number; disallowed_count: number; effective_count: number };
  provider_access: { allowed_count: number; disallowed_count: number };
  operation_types: {
    choices: Array<{ value: string; label: string }>;
    applications: Array<{
      id: string;
      name: string;
      environment: string;
      is_active: boolean;
      allowed_scopes: string[];
    }>;
  };
}

// ---- Settlements ----
export interface SettlementWalletPosition {
  wallet: string;
  country_code: string;
  country_name: string;
  currency: string;
  balance: string;
  available: string;
  reserved: string;
  settleable: string;
  minimum: string;
  minimum_system: string;
  system_currency: string;
  reserving_operations: string[];
  fx_error: string;
}

export interface SettlementLimits {
  system_currency: string;
  system_min_amount: string;
  min_amount: string | null;
  bulk_system_max_lines: number;
  bulk_max_lines: number | null;
  bulk_effective_max_lines: number;
}

export interface SettlementsOverview {
  wallets: SettlementWalletPosition[];
  accounts: Array<{
    id: string;
    label: string;
    kind: 'FINANCIAL_ENTITY' | 'PARTNER' | 'BANK';
    currency: string;
    country_code: string;
    provider: string;
    partner: string;
    payout_method: string;
    details: Record<string, string>;
    is_default: boolean;
    created_at: string;
  }>;
  schedules: Array<{
    id: string;
    name: string;
    frequency: string;
    interval_days: number | null;
    weekday: number | null;
    day_of_month: number | null;
    currency: string;
    account: string;
    min_amount: string;
    is_active: boolean;
    next_run_at: string | null;
    last_run_at: string | null;
    last_status: string;
    last_message: string;
  }>;
  pending_count: number;
  overdue_count: number;
  limits: SettlementLimits;
}

// ---- Developer ----
export interface DeveloperResponse {
  webhook: { url: string; has_secret: boolean };
  applications: Array<{
    id: string;
    name: string;
    client_id: string;
    environment: string;
    is_active: boolean;
    key_count: number;
    active_key_count: number;
    allowed_ips: string[];
    allowed_scopes: string[];
    allowed_sms_sender_names?: string[];
    description?: string;
    last_used_at?: string | null;
    created_at?: string;
  }>;
  webhook_rules: Array<{
    id: string;
    name: string;
    status: string;
    provider_code: string;
    event_type: string;
    application_id: string | null;
    application_name: string;
    webhook_url: string;
    priority: number;
    max_retries: number;
  }>;
}

// ---- Members ----
export interface MemberRow {
  id: string;
  email: string;
  name: string;
  role_code: string;
  last_login: string | null;
  is_active: boolean;
}

export interface MembersResponse {
  active: MemberRow[];
  pending: Array<Record<string, unknown> & { id: string; email: string; role_code: string }>;
  disabled: MemberRow[];
  available_roles: Array<{ code: string; label: string }>;
}

// ---- Security ----
export interface IpRule {
  id: string;
  value: string;
  kind: 'address' | 'range';
  note: string;
  expires_at: string | null;
  is_active: boolean;
  is_effective: boolean;
  created_at: string;
}

export interface SecurityResponse {
  enforce_2fa: boolean;
  ip_rules: IpRule[];
  ip_restriction_active: boolean;
  applications: Array<{ id: string; name: string; environment: string; allowed_ips: string[] }>;
}

// ---- Operations ----
export interface OperationsResponse {
  wallets: Array<{
    id: string;
    currency_code: string;
    country_code: string;
    balance: string;
    reserved_balance: string;
    is_active: boolean;
    ledger_blocked: boolean;
    ledger_blocked_at: string | null;
    ledger_block_reason: string;
  }>;
  topups: Array<{
    id: string;
    amount: string;
    currency_code: string;
    funding_source: string;
    reference: string;
    notes: string;
    topped_up_at: string;
  }>;
  adjustments: Array<{
    id: string;
    reference: string;
    description: string;
    direction: string;
    amount: string;
    currency_code: string;
    reason: string;
    created_at: string;
  }>;
}

export interface AuditRow {
  id: number;
  action: string;
  entity_type: string;
  entity_id: string;
  user_id: string | null;
  changes: Record<string, unknown>;
  reason: string;
  created_at: string;
}

/**
 * HTTP client for the back-office merchant hub (`auth/merchants/{id}/...`).
 */
@Injectable({ providedIn: 'root' })
export class MerchantHubService {
  private readonly api = inject(ApiService);

  private path(id: string, tail: string): string {
    return `auth/merchants/${id}/${tail}`;
  }

  getMerchant(id: string): Observable<HubMerchant> {
    return this.api.get<HubMerchant>(`auth/merchants/${id}/`);
  }

  // Lifecycle
  block(id: string, reason: string): Observable<HubMerchant> {
    return this.api.post<HubMerchant>(this.path(id, 'block/'), { reason });
  }
  unblock(id: string, reason = ''): Observable<HubMerchant> {
    return this.api.post<HubMerchant>(this.path(id, 'unblock/'), { reason });
  }
  archive(id: string, reason: string): Observable<HubMerchant> {
    return this.api.post<HubMerchant>(this.path(id, 'archive/'), { reason });
  }
  restore(id: string, reason = ''): Observable<HubMerchant> {
    return this.api.post<HubMerchant>(this.path(id, 'restore/'), { reason });
  }

  // Overview
  overview(id: string, days: number): Observable<OverviewResponse> {
    return this.api.get<OverviewResponse>(this.path(id, 'overview/'), { days });
  }

  // Fees
  fees(id: string): Observable<FeesResponse> {
    return this.api.get<FeesResponse>(this.path(id, 'fees/'));
  }
  overrideFee(id: string, payload: FeeOverridePayload): Observable<FeesResponse> {
    return this.api.post<FeesResponse>(this.path(id, 'fees/override/'), payload);
  }
  resetFees(
    id: string,
    payload: { operation_type?: string | null; include_switches?: boolean; reason?: string },
  ): Observable<FeesResponse & { removed_rules: number; switches_reset: number }> {
    return this.api.post<FeesResponse & { removed_rules: number; switches_reset: number }>(
      this.path(id, 'fees/reset/'),
      payload,
    );
  }
  setFeeSwitch(
    id: string,
    payload: { operation_type: string; fees_enabled: boolean; reason?: string },
  ): Observable<{ switches: FeeSwitchRow[] }> {
    return this.api.put<{ switches: FeeSwitchRow[] }>(this.path(id, 'fees/switches/'), payload);
  }
  serviceFees(id: string): Observable<ServiceFeesResponse> {
    return this.api.get<ServiceFeesResponse>(this.path(id, 'fees/services/'));
  }

  // Profile (multipart: the logo is a file)
  profile(id: string): Observable<MerchantProfile> {
    return this.api.get<MerchantProfile>(this.path(id, 'profile/'));
  }
  updateProfile(id: string, payload: FormData): Observable<MerchantProfile> {
    return this.api.patch<MerchantProfile>(this.path(id, 'profile/'), payload);
  }

  // Configuration
  configuration(id: string): Observable<ConfigurationResponse> {
    return this.api.get<ConfigurationResponse>(this.path(id, 'configuration/'));
  }
  updateConfiguration(
    id: string,
    payload: Record<string, unknown>,
  ): Observable<ConfigurationResponse> {
    return this.api.patch<ConfigurationResponse>(this.path(id, 'configuration/'), payload);
  }
  configurationCountries(
    id: string,
    params: { page: number; search?: string; activated?: boolean },
  ): Observable<HubPage<ConfigurationCountry>> {
    return this.api.get<HubPage<ConfigurationCountry>>(this.path(id, 'configuration/countries/'), {
      page: params.page,
      ...(params.search ? { search: params.search } : {}),
      ...(params.activated === undefined ? {} : { activated: String(params.activated) }),
    });
  }
  configurationProviders(
    id: string,
    params: {
      page: number;
      search?: string;
      country?: string;
      country_code?: string;
      entity_type?: string;
      currency?: string;
      status?: string;
    },
  ): Observable<ConfigurationProviderPage> {
    const query: Record<string, string | number> = { page: params.page };
    for (const key of ['search', 'country', 'country_code', 'entity_type', 'currency', 'status'] as const) {
      const value = params[key];
      if (value) {
        query[key] = value;
      }
    }
    return this.api.get<ConfigurationProviderPage>(this.path(id, 'configuration/providers/'), query);
  }

  // Wallets (finances API, scoped to the merchant)
  merchantWallets(
    id: string,
    params: { page: number; search?: string },
  ): Observable<HubPage<HubWallet>> {
    return this.api.get<HubPage<HubWallet>>('finances/wallets/', {
      merchant: id,
      page: params.page,
      ...(params.search ? { search: params.search } : {}),
    });
  }
  availableMarkets(
    id: string,
    params: { page: number; search?: string },
  ): Observable<HubPage<AvailableMarket>> {
    return this.api.get<HubPage<AvailableMarket>>('finances/wallets/available-markets/', {
      merchant: id,
      page: params.page,
      ...(params.search ? { search: params.search } : {}),
    });
  }
  createWallet(payload: {
    merchant_id: string;
    country: number | string;
    currency: number | string;
  }): Observable<unknown> {
    return this.api.post<unknown>('finances/wallets/', payload);
  }
  setOperationCountries(
    walletId: string,
    countryIds: Array<number | string>,
  ): Observable<HubWallet> {
    return this.api.patch<HubWallet>(`finances/wallets/${walletId}/operation-countries/`, {
      country_ids: countryIds,
    });
  }

  // Developer
  developer(id: string): Observable<DeveloperResponse> {
    return this.api.get<DeveloperResponse>(this.path(id, 'developer/'));
  }

  // Members
  members(id: string): Observable<MembersResponse> {
    return this.api.get<MembersResponse>(this.path(id, 'members/'));
  }
  inviteMember(
    id: string,
    payload: { email: string; name: string; role_code: string },
  ): Observable<unknown> {
    return this.api.post<unknown>(this.path(id, 'members/invite/'), payload);
  }
  revokeInvite(id: string, inviteId: string): Observable<void> {
    return this.api.delete(this.path(id, `members/invites/${inviteId}/`));
  }
  changeMemberRole(id: string, userId: string, roleCode: string): Observable<MembersResponse> {
    return this.api.put<MembersResponse>(this.path(id, `members/${userId}/role/`), {
      role_code: roleCode,
    });
  }
  setMemberAccess(id: string, userId: string, isActive: boolean): Observable<MembersResponse> {
    return this.api.put<MembersResponse>(this.path(id, `members/${userId}/access/`), {
      is_active: isActive,
    });
  }

  // Security
  security(id: string): Observable<SecurityResponse> {
    return this.api.get<SecurityResponse>(this.path(id, 'security/'));
  }
  addIpRule(
    id: string,
    payload: { value: string; note?: string; expires_at?: string | null },
  ): Observable<IpRule> {
    return this.api.post<IpRule>(this.path(id, 'security/ip-rules/'), payload);
  }
  removeIpRule(id: string, ruleId: string): Observable<void> {
    return this.api.delete(this.path(id, `security/ip-rules/${ruleId}/`));
  }
  setEnforce2fa(id: string, enforce: boolean, reason = ''): Observable<SecurityResponse> {
    return this.api.put<SecurityResponse>(this.path(id, 'security/2fa/'), { enforce, reason });
  }

  // Operations
  operations(id: string): Observable<OperationsResponse> {
    return this.api.get<OperationsResponse>(this.path(id, 'operations/'));
  }
  adjustWallet(
    id: string,
    payload: { wallet_id: string; direction: 'CREDIT' | 'DEBIT'; amount: string; reason: string },
  ): Observable<OperationsResponse> {
    return this.api.post<OperationsResponse>(this.path(id, 'operations/adjustments/'), payload);
  }
  unblockWallet(id: string, walletId: string, notes: string): Observable<OperationsResponse> {
    return this.api.post<OperationsResponse>(this.path(id, 'operations/wallet-unblock/'), {
      wallet_id: walletId,
      notes,
    });
  }

  // Settlements
  settlements(id: string): Observable<SettlementsOverview> {
    return this.api.get<SettlementsOverview>(this.path(id, 'settlements/'));
  }
  setSettlementLimits(
    id: string,
    payload: { min_amount: string | null; bulk_max_lines: number | null },
  ): Observable<SettlementsOverview> {
    return this.api.put<SettlementsOverview>(this.path(id, 'settlements/limits/'), payload);
  }

  // Audit
  audit(id: string, limit = 50, actionPrefix = ''): Observable<{ results: AuditRow[] }> {
    return this.api.get<{ results: AuditRow[] }>(this.path(id, 'audit/'), {
      limit,
      action: actionPrefix,
    });
  }
}
